﻿// GanttChart.jsx
import React, { useState, useRef, useMemo, useEffect } from 'react'
import { useAuth } from '../hooks/useAuth'
import { DEPT_COLORS, DEPT_PRIORITY } from '../lib/deptColors'
import { sortProjects, isDepartureOverdue, isArrivalOverdue } from '../lib/constants'
import ExportButton from './ExportButton'
import { getOrgHolidays, isOffDay } from '../lib/businessDays'
import { QA_MILESTONE_NAMES } from '../lib/qaMilestones'
import { legsTooltip } from '../lib/flightUtils'

const LABEL_WIDTH_DESKTOP = 340
const LABEL_WIDTH_MOBILE  = 200
const HOME_FBO = 'AAO'

// Returns all departments for a subtask, supporting both the legacy single `department` field and the newer `departments` array
function subtaskDepts(s) {
  return s.departments && s.departments.length > 0 ? s.departments : [s.department]
}

// Build segments â€” each segment now carries ALL active depts for multi-dept rendering
function getSegments(project, projectSubtasks) {
  // Pure-QA tasks are milestone/inspection markers â€” exclude from bar coloring.
  // Multi-dept tasks that include QA alongside other depts are kept; QA is stripped
  // when collecting depts below so only the non-QA departments color the bar.
  projectSubtasks = projectSubtasks.filter(s => subtaskDepts(s).some(d => d !== 'QA'))
  // Also strip QA from the project-level dept list used for gap/fallback segments
  const projectDepts = (project.departments || ['MX']).filter(d => d !== 'QA')
  // Sort fallbackDepts by DEPT_PRIORITY so gap segments have consistent ordering with active segments
  const fallbackDepts = [...projectDepts].sort((a, b) => DEPT_PRIORITY.indexOf(a) - DEPT_PRIORITY.indexOf(b))
  if (fallbackDepts.length === 0) fallbackDepts.push('MX')

  const start = new Date(project.arrival_date + 'T00:00:00')
  start.setHours(0, 0, 0, 0)
  const end = project.departure_date
    ? new Date(new Date(project.departure_date + 'T00:00:00').getTime() + 86400000)
    : new Date(start.getTime() + 7 * 86400000)
  end.setHours(0, 0, 0, 0)

  if (projectSubtasks.length === 0) {
    return [{ start: start.getTime(), end: end.getTime(), depts: fallbackDepts }]
  }

  const points = new Set([start.getTime(), end.getTime()])
  projectSubtasks.forEach(s => {
    const sd = new Date(s.start_date + 'T00:00:00'); sd.setHours(0, 0, 0, 0)
    const ed = new Date(s.end_date + 'T00:00:00'); ed.setHours(0, 0, 0, 0)
    // Add start point
    if (sd.getTime() >= start.getTime() && sd.getTime() <= end.getTime()) points.add(sd.getTime())
    // Add day after end to capture full last day
    const edNext = new Date(ed.getTime() + 86400000)
    if (edNext.getTime() >= start.getTime() && edNext.getTime() <= end.getTime()) points.add(edNext.getTime())
  })

  const sorted = Array.from(points).sort((a, b) => a - b)
  const segments = []

  for (let i = 0; i < sorted.length - 1; i++) {
    const segStart = sorted[i]
    const segEnd = sorted[i + 1]
    const midpoint = (segStart + segEnd) / 2

    const active = projectSubtasks.filter(s => {
      const sd = new Date(s.start_date + 'T00:00:00'); sd.setHours(0, 0, 0, 0)
      const ed = new Date(s.end_date + 'T00:00:00'); ed.setHours(0, 0, 0, 0)
      return sd.getTime() <= midpoint && (ed.getTime() + 86400000) > midpoint
    })

    if (active.length === 0) {
      // Gap segments (no active subtask) always use the project's assigned departments.
      // Milestone subtasks should only color the specific days they cover, not bleed into surrounding gaps.
      segments.push({ start: segStart, end: segEnd, depts: fallbackDepts })
    } else {
      // Sort by dept priority, collect unique depts â€” sort the final list too so
      // multi-dept subtasks with non-priority-ordered internal arrays don't flip colors
      const sortedActive = [...active].sort((a, b) =>
        DEPT_PRIORITY.indexOf(subtaskDepts(a)[0]) - DEPT_PRIORITY.indexOf(subtaskDepts(b)[0])
      )
      const depts = [...new Set(sortedActive.flatMap(s => subtaskDepts(s).filter(d => d !== 'QA')))]
        .sort((a, b) => DEPT_PRIORITY.indexOf(a) - DEPT_PRIORITY.indexOf(b))
      segments.push({ start: segStart, end: segEnd, depts })
    }
  }

  return segments
}

// Render a single segment â€” handles 1, 2, 3, or 4 depts with SVG patterns
function SegmentFill({ depts }) {
  const { profile } = useAuth()
  const orgDeptMap = useMemo(() => Object.fromEntries((profile?.orgs?.settings?.departments ?? []).map(d => [d.key, d])), [profile?.orgs?.id])
  const colors = depts.map(d => orgDeptMap[d]?.hex || '#9ca3af')

  if (depts.length === 1) {
    return <div className="w-full h-full" style={{ backgroundColor: colors[0] }} />
  }

  // Stack horizontal stripes â€” equal height per dept
  return (
    <div className="w-full h-full flex flex-col">
      {colors.map((color, i) => (
        <div key={i} className="flex-1" style={{ backgroundColor: color }} />
      ))}
    </div>
  )
}

function GanttBar({ segments, projectStart, projectEnd, totalStart, totalEnd, onClick, isTBD, showEndCap = true, showStartCap = true }) {
  const totalMs = totalEnd - totalStart
  const barLeft = ((projectStart - totalStart) / totalMs) * 100
  const barWidth = ((projectEnd - projectStart) / totalMs) * 100

  if (barWidth <= 0) return null

  const barDuration = projectEnd - projectStart

  return (
    <div
      className="absolute top-1/2 -translate-y-1/2 cursor-pointer group"
      style={{ left: `${barLeft}%`, width: `${barWidth}%`, height: 28 }}
      onClick={onClick}
    >
      {isTBD && (
        <div className="absolute inset-0 rounded-md border-2 border-yellow-400 animate-pulse z-20 pointer-events-none" />
      )}
      <div className="relative w-full h-full flex overflow-hidden rounded-md shadow-sm group-hover:opacity-90 transition-opacity">
        {showStartCap && (
          <div className="absolute left-0 top-0 bottom-0 z-10 pointer-events-none rounded-l-md"
            style={{ width: 5, backgroundColor: '#86efac' }} />
        )}
        {segments.map((seg, i) => {
          const segLeft = ((seg.start - projectStart) / barDuration) * 100
          const segWidth = Math.max(((seg.end - seg.start) / barDuration) * 100, 0.5) // min 0.5% so 1-day segments show
          return (
            <div
              key={i}
              className="h-full absolute top-0 overflow-hidden"
              style={{ left: `${segLeft}%`, width: `${segWidth}%` }}
            >
              <SegmentFill depts={seg.depts} height={28} />
            </div>
          )
        })}
        {showEndCap && (
          <div className="absolute right-0 top-0 bottom-0 z-10 pointer-events-none rounded-r-md"
            style={{ width: 5, backgroundColor: '#f87171' }} />
        )}
      </div>
    </div>
  )
}

function formatDateShort(d) {
  if (!d) return 'â€”'
  return new Date(d + 'T00:00:00').toLocaleDateString('en-US', { month: 'short', day: 'numeric' })
}

const INSP_FULL_NAMES = { S: '200-Hr Special Inspection', A: 'A Inspection', B: 'B Inspection', C: 'C Inspection', D: 'D Inspection', HSI: 'Hot Section Inspection' }
function inspLabelBg() {
  return '#0f766e'  // teal-700 for all inspection due date callouts
}

const PRIORITY_HANGARS = ['C3', 'C4', 'C6']

function MultiSelectDropdown({ options, selected, onToggle }) {
  const [open, setOpen] = useState(false)
  const ref = useRef(null)

  useEffect(() => {
    if (!open) return
    const handler = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false) }
    document.addEventListener('mousedown', handler)
    return () => document.removeEventListener('mousedown', handler)
  }, [open])

  const label = selected.size === 0 ? 'All'
    : selected.size <= 2 ? [...selected].join(', ')
    : `${selected.size} selected`

  return (
    <div ref={ref} className="relative">
      <button
        onClick={() => setOpen(o => !o)}
        className="flex items-center gap-1 px-2.5 py-1 bg-gray-100 hover:bg-gray-200 rounded-lg text-xs font-medium text-gray-700 transition-colors min-w-[60px]"
      >
        <span className="flex-1 text-left">{label}</span>
        <svg className={`w-3 h-3 text-gray-400 flex-shrink-0 transition-transform ${open ? 'rotate-180' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
        </svg>
      </button>
      {open && (
        <div className="absolute left-0 top-full mt-1 bg-white border border-gray-200 rounded-lg shadow-lg z-50 min-w-[120px] py-1">
          {options.map(opt => (
            <button
              key={opt}
              onClick={() => onToggle(opt)}
              className="flex items-center gap-2 w-full px-3 py-1.5 text-xs text-gray-700 hover:bg-gray-50 text-left"
            >
              <div className={`w-3.5 h-3.5 rounded border flex-shrink-0 flex items-center justify-center ${selected.has(opt) ? 'bg-brand-red border-brand-red' : 'border-gray-300'}`}>
                {selected.has(opt) && (
                  <svg className="w-2.5 h-2.5 text-white" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                  </svg>
                )}
              </div>
              {opt}
            </button>
          ))}
        </div>
      )}
    </div>
  )
}

export default function GanttChart({ projects = [], subtasks = [], filters, onClickProject, onClickSubtask, onClose, aircraft = [], search, setSearch, onNavigateToWorkflow, onUpdateProjectDates, flightLegs = [], inspectionDueDates = {}, toleranceExpiryDates = {}, orgAircraftTypes, orgDepartments }) {
  const isMobile = typeof window !== 'undefined' && window.innerWidth < 640
  const LABEL_WIDTH = isMobile ? LABEL_WIDTH_MOBILE : LABEL_WIDTH_DESKTOP
  const today = new Date()
  today.setHours(0, 0, 0, 0)
  const { profile } = useAuth()
  const weekStartDay = profile?.orgs?.settings?.scheduling?.weekStartDay ?? 1
  const [editingDate, setEditingDate] = useState(null)
  const [showFlights, setShowFlights] = useState(true)
  const baseStart = useRef(new Date(2026, 0, 1)).current
  const totalScrollDays = 365 * 2

  const [scrollOffset, setScrollOffset] = useState(() => {
    const base = new Date(2026, 0, 1)
    const d = new Date(today)
    d.setDate(d.getDate() - ((d.getDay() - weekStartDay + 7) % 7))
    return Math.max(0, Math.round((d - base) / 86400000))
  })

  const [expanded, setExpanded] = useState(new Set())
  // Tracks non-default group states. Default: Complete=closed, In Work/Scheduled=open.
  const [groupOverrides, setGroupOverrides] = useState(new Set())

  const isGroupOpen = (projectId, status) => {
    const key = `${projectId}-${status}`
    const defaultOpen = status !== 'Complete'
    return groupOverrides.has(key) ? !defaultOpen : defaultOpen
  }
  const toggleGroup = (projectId, status) => {
    const key = `${projectId}-${status}`
    setGroupOverrides(prev => {
      const next = new Set(prev)
      next.has(key) ? next.delete(key) : next.add(key)
      return next
    })
  }
  const typeOptions = orgAircraftTypes ?? ['Jet', 'Piston']
  const deptOptions = orgDepartments
    ? orgDepartments.filter(d => d.key !== 'QA')
    : Object.keys(DEPT_COLORS).filter(d => d !== 'QA').map(d => ({ key: d, label: d, hex: DEPT_COLORS[d].hex }))

  const orgDeptMap = useMemo(() => Object.fromEntries((orgDepartments ?? []).map(d => [d.key, d])), [orgDepartments])

  const [typeFilter,   setTypeFilter]   = useState(new Set())
  const [deptFilter,   setDeptFilter]   = useState(new Set())
  const [hangarFilter, setHangarFilter] = useState(new Set())

  const toggleType   = (t) => setTypeFilter(p   => { const n = new Set(p); n.has(t) ? n.delete(t) : n.add(t); return n })
  const toggleDept   = (d) => setDeptFilter(p   => { const n = new Set(p); n.has(d) ? n.delete(d) : n.add(d); return n })
  const toggleHangar = (h) => setHangarFilter(p => { const n = new Set(p); n.has(h) ? n.delete(h) : n.add(h); return n })

  const hangarOptions = useMemo(() => {
    const vals = [...new Set(projects.map(p => p.hangar_location?.trim().toUpperCase()).filter(Boolean))]
    return vals.sort((a, b) => {
      const ai = PRIORITY_HANGARS.indexOf(a), bi = PRIORITY_HANGARS.indexOf(b)
      if (ai !== -1 && bi !== -1) return ai - bi
      if (ai !== -1) return -1
      if (bi !== -1) return 1
      return a.localeCompare(b)
    })
  }, [projects])

  const [sortBy, setSortBy] = useState(() =>
    sessionStorage.getItem('ganttSortBy') || 'departure'
  )

  const handleSetSortBy = (val) => {
    sessionStorage.setItem('ganttSortBy', val)
    setSortBy(val)
  }

  const viewStart = new Date(baseStart.getTime() + scrollOffset * 86400000)
  viewStart.setHours(0, 0, 0, 0)
  const viewEnd = new Date(viewStart)
  if (isMobile) {
    viewEnd.setDate(viewEnd.getDate() + 7)
  } else {
    viewEnd.setMonth(viewEnd.getMonth() + 1)
  }
  const totalDays = Math.round((viewEnd - viewStart) / 86400000)

  const navigate = (dir) => {
    const step = isMobile ? 7 : 30
    setScrollOffset(prev => Math.max(0, Math.min(prev + dir * step, totalScrollDays)))
  }

  const goToday = () => {
    const d = new Date(today)
    d.setDate(d.getDate() - ((d.getDay() - weekStartDay + 7) % 7))
    setScrollOffset(Math.max(0, Math.round((d - baseStart) / 86400000)))
  }

  const days = []
  const orgHolidays     = getOrgHolidays(viewStart.getFullYear())
  const orgHolidaysNext = getOrgHolidays(viewStart.getFullYear() + 1)
  const allOrgHolidays  = new Set([...orgHolidays, ...orgHolidaysNext])

  for (let i = 0; i < totalDays; i++) {
    const d = new Date(viewStart)
    d.setDate(viewStart.getDate() + i)
    const iso = d.toISOString().slice(0, 10)
    const isWeekend = d.getDay() === 0 || d.getDay() === 6
    const isHoliday = allOrgHolidays.has(iso)
    days.push({ date: d, iso, isWeekend, isHoliday, isOff: isOffDay(d) })
  }

  const monthGroups = []
  days.forEach(d => {
    const label = d.date.toLocaleDateString('en-US', { month: 'long', year: 'numeric' })
    if (!monthGroups.length || monthGroups[monthGroups.length - 1].label !== label) {
      monthGroups.push({ label, count: 1 })
    } else {
      monthGroups[monthGroups.length - 1].count++
    }
  })

  const todayOffset = (today.getTime() - viewStart.getTime()) / 86400000
  const todayPct = (todayOffset / totalDays) * 100
  const showToday = todayPct >= 0 && todayPct <= 100

  const toggle = (id) => setExpanded(prev => {
    const next = new Set(prev)
    next.has(id) ? next.delete(id) : next.add(id)
    return next
  })

  const ROW_HEIGHT = 48
  const SUBROW_HEIGHT = 36

  const visibleProjects = projects.filter(p => {
  if (p.status === 'Complete') return false
  if (typeFilter.size > 0 && !typeFilter.has(p.aircraft?.aircraft_type || 'Jet')) return false
  if (deptFilter.size > 0) {
    const projectSubtasks = subtasks.filter(s => s.project_id === p.id)
    if (!(p.departments || ['MX']).some(d => deptFilter.has(d)) && !projectSubtasks.some(s => subtaskDepts(s).some(d => deptFilter.has(d)))) return false
  }
  if (hangarFilter.size > 0 && !hangarFilter.has(p.hangar_location?.trim().toUpperCase() || '')) return false
  if (search) {
    const term = search.toLowerCase()
    const reg  = (p.aircraft?.registration || '').toLowerCase()
    const alt  = (p.secondary_registration || '').toLowerCase()
    const customer  = (p.customer_name || '').toLowerCase()
    const workscope = (p.workscope || '').toLowerCase()
    if (!reg.includes(term) && !alt.includes(term) && !customer.includes(term) && !workscope.includes(term)) return false
  }
  return true
})
  const sortedProjects = sortProjects(visibleProjects, sortBy)

  const flightsByReg = React.useMemo(() => {
    const map = {}
    for (const leg of flightLegs) {
      if (!leg.aircraft_reg) continue
      if (!map[leg.aircraft_reg]) map[leg.aircraft_reg] = {}
      if (!map[leg.aircraft_reg][leg.trip_id]) map[leg.aircraft_reg][leg.trip_id] = []
      map[leg.aircraft_reg][leg.trip_id].push(leg)
    }
    return map
  }, [flightLegs])

  return (
    <div id="gantt-export" className="bg-white rounded-xl border border-gray-200 shadow-sm">

      {/* Sticky toolbar + scrollbar */}
      <div className="no-print sticky top-0 z-20">
        {/* Row 1: search, navigation, filters, export */}
        <div className="flex items-center gap-3 px-4 pt-3 pb-2 bg-white">
          <div className="relative">
            <svg className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
            </svg>
            <input
              type="text"
              placeholder="Search registration, customer..."
              value={search}
              onChange={e => setSearch(e.target.value)}
              className="pl-9 pr-3 py-1.5 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-brand-red/20 w-40 sm:w-52"
            />
          </div>
          <button onClick={goToday} className="text-xs font-medium px-3 py-1.5 rounded-lg border border-gray-200 hover:bg-gray-50 transition-colors">
            Today
          </button>
          <div className="flex items-center gap-1">
            <button onClick={() => navigate(-1)} className="p-1.5 rounded-lg hover:bg-gray-100 transition-colors text-gray-500">
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
              </svg>
            </button>
            <span className="text-sm font-medium text-gray-700 text-center" style={{ minWidth: isMobile ? 130 : 160 }}>
              {isMobile
                ? `${viewStart.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })} â€“ ${new Date(viewStart.getTime() + 6 * 86400000).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}`
                : viewStart.toLocaleDateString('en-US', { month: 'long', year: 'numeric' })}
            </span>
            <button onClick={() => navigate(1)} className="p-1.5 rounded-lg hover:bg-gray-100 transition-colors text-gray-500">
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
              </svg>
            </button>
          </div>
          <div className="hidden sm:flex items-center gap-3">
            <div className="flex items-center gap-2">
              <span className="text-xs font-medium text-gray-500">Type</span>
              <MultiSelectDropdown options={typeOptions} selected={typeFilter} onToggle={toggleType} />
            </div>
            <div className="flex items-center gap-2">
              <span className="text-xs font-medium text-gray-500">Hangar</span>
              <MultiSelectDropdown options={hangarOptions} selected={hangarFilter} onToggle={toggleHangar} />
            </div>
          </div>
          <div className="hidden sm:flex ml-auto flex-shrink-0">
            <ExportButton elementId="gantt-export" filename={`gantt-${new Date().toISOString().slice(0, 10)}`} showTechView={false} />
          </div>
        </div>

        {/* Row 2: department filter buttons */}
        <div className="hidden sm:flex items-center gap-1 flex-wrap px-4 pb-2 bg-white">
          {deptOptions.map(dept => {
            const isActive = deptFilter.size === 0 || deptFilter.has(dept.key)
            return (
              <button
                key={dept.key}
                onClick={() => toggleDept(dept.key)}
                className="px-2 py-0.5 rounded text-xs font-medium text-white transition-opacity"
                style={{ backgroundColor: dept.hex, opacity: isActive ? 1 : 0.3 }}
              >
                {dept.key}
              </button>
            )
          })}
        </div>

        <div className="hidden sm:flex px-4 py-2 border-b border-gray-200 bg-gray-50 items-center gap-3">
          <span className="text-xs text-gray-400 flex-shrink-0 w-20">Jan 2026</span>
          <input type="range" min={0} max={totalScrollDays} value={scrollOffset}
            onChange={e => setScrollOffset(Number(e.target.value))}
            className="flex-1 h-2 rounded-lg appearance-none cursor-pointer accent-brand-red" />
          <span className="text-xs text-gray-400 flex-shrink-0 w-20 text-right">
            {new Date(baseStart.getTime() + totalScrollDays * 86400000)
              .toLocaleDateString('en-US', { month: 'short', year: 'numeric' })}
          </span>
        </div>
      </div>

      {/* Gantt body */}
      <div className="overflow-x-auto" style={{ maxHeight: 'calc(100vh - 200px)', overflowY: 'auto' }}>
        <div style={isMobile ? undefined : { minWidth: 900 }}>

          {/* Header */}
          <div className="flex border-b border-gray-200 sticky top-0 z-10 bg-white">
            <div className="flex-shrink-0 border-r border-gray-200 bg-gray-50" style={{ width: LABEL_WIDTH }}>
              <div className="h-8 border-b border-gray-200" />
              <div className="h-8 flex items-center">
                <div className="w-6 flex-shrink-0" />
                <div className="flex-shrink-0 text-xs font-semibold text-gray-500 uppercase tracking-wide flex items-center" style={{ width: isMobile ? 80 : 140 }}>Aircraft</div>
                <button onClick={() => handleSetSortBy('arrival')} className={`flex-shrink-0 text-xs font-semibold uppercase tracking-wide flex items-center justify-center gap-0.5 transition-colors ${isMobile ? 'w-12' : 'w-20'} ${sortBy === 'arrival' ? 'text-brand-red' : 'text-gray-500 hover:text-gray-700'}`}>
                  Start{sortBy === 'arrival' && <svg className="w-3 h-3 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M5 15l7-7 7 7" /></svg>}
                </button>
                <button onClick={() => handleSetSortBy('departure')} className={`flex-shrink-0 text-xs font-semibold uppercase tracking-wide flex items-center justify-center gap-0.5 transition-colors ${isMobile ? 'w-12' : 'w-20'} ${sortBy === 'departure' ? 'text-brand-red' : 'text-gray-500 hover:text-gray-700'}`}>
                  End{sortBy === 'departure' && <svg className="w-3 h-3 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M5 15l7-7 7 7" /></svg>}
                </button>
              </div>
            </div>
            <div className="flex-1 relative overflow-hidden">
              <div className="flex h-8 border-b border-gray-200">
                {monthGroups.map((g, i) => (
                  <div key={i} className="flex-shrink-0 border-r border-gray-200 px-2 flex items-center bg-gray-50"
                    style={{ width: `${(g.count / totalDays) * 100}%` }}>
                    <span className="text-xs font-semibold text-gray-600 truncate">{g.label}</span>
                  </div>
                ))}
              </div>
              <div className="flex h-8 relative">
                {days.map((d, i) => {
                  const isToday = d.date.getTime() === today.getTime()
                  return (
                    <div key={i} className="flex-shrink-0 border-r border-gray-100 flex items-center justify-center"
                      style={{ width: `${(1 / totalDays) * 100}%`, backgroundColor: d.isOff ? '#e5e7eb' : 'white' }}>
                      <span className={`text-xs select-none ${
                        isToday ? 'font-bold text-brand-red' :
                        d.isHoliday ? 'text-red-400 font-medium' : 'text-gray-400'
                      }`}>
                        {d.date.getDate()}
                      </span>
                    </div>
                  )
                })}
                {showToday && (
                  <div className="absolute top-0 bottom-0 w-0.5 bg-brand-red z-10 pointer-events-none"
                    style={{ left: `${todayPct}%` }} />
                )}
              </div>
            </div>

          </div>

          {/* Rows */}
          {sortedProjects.length === 0 && (
            <div className="flex items-center justify-center py-16 text-gray-400 text-sm">
              No active projects
            </div>
          )}

          {sortedProjects.map(project => {
            const isTBD = project.status === 'TBD' || !project.departure_date
            const arrivalOverdue = isArrivalOverdue(project)
            const departureOverdue = isDepartureOverdue(project)
            const projectSubtasks = subtasks.filter(s =>
              s.project_id === project.id &&
              !(s.department === 'QA' && QA_MILESTONE_NAMES.has(s.task_name))
            )
            const visibleSubtasks = filters?.department !== 'All'
              ? projectSubtasks.filter(s => subtaskDepts(s).includes(filters.department))
              : projectSubtasks
            const isExpanded = expanded.has(project.id)

            const pStart = new Date(project.arrival_date + 'T00:00:00')
            pStart.setHours(0, 0, 0, 0)
            const pEnd = project.departure_date
              ? new Date(project.departure_date + 'T00:00:00')
              : new Date(pStart.getTime() + 7 * 86400000)
            pEnd.setHours(0, 0, 0, 0)
            const pEndDisplay = new Date(pEnd.getTime() + 86400000)

            const segments = getSegments(project, projectSubtasks)
            const clampedStart = Math.max(pStart.getTime(), viewStart.getTime())
            const clampedEnd = Math.min(pEndDisplay.getTime(), viewEnd.getTime())
            const showEndCap = pEnd.getTime() < viewEnd.getTime()
            const showStartCap = pStart.getTime() >= viewStart.getTime()
            const inView = pEnd >= viewStart && pStart <= viewEnd

            return (
              <div key={project.id}>
                <div className={`flex border-b border-gray-100 hover:bg-gray-50/50 transition-colors ${isTBD ? 'bg-yellow-50/30' : ''}`}
                  style={{ height: ROW_HEIGHT, position: 'relative' }}>
                  <div className="no-screen" style={{
                    position: 'absolute', left: 0, top: 0, bottom: 0,
                    width: LABEL_WIDTH, backgroundColor: 'white', zIndex: 50, pointerEvents: 'none',
                  }} />

                  <div className="flex-shrink-0 border-r border-gray-200 flex items-center" style={{ width: LABEL_WIDTH }}>
                    <div className="w-6 flex justify-center flex-shrink-0">
                      {projectSubtasks.length > 0 ? (
                        <button onClick={() => toggle(project.id)}
                          className="w-5 h-5 flex items-center justify-center text-gray-400 hover:text-gray-700 transition-colors">
                          <svg className={`w-3.5 h-3.5 transition-transform ${isExpanded ? 'rotate-90' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                          </svg>
                        </button>
                      ) : <div className="w-5" />}
                    </div>
                    <div className="flex-shrink-0 px-1 py-0.5 rounded" style={{ width: isMobile ? 80 : 140, backgroundColor: isTBD ? '#fde047' : 'transparent' }}>
                      <div
                        className={`text-base font-bold truncate transition-colors flex items-center gap-1 ${project.firm_schedule ? 'text-red-600' : 'text-gray-800'} ${onNavigateToWorkflow ? 'cursor-pointer hover:text-brand-red' : ''}`}
                        onClick={onNavigateToWorkflow ? () => onNavigateToWorkflow(project.id) : undefined}
                        title={project.firm_schedule ? 'This project has a firm schedule' : onNavigateToWorkflow ? 'Open project workflow' : undefined}
                      >
                        {project.aircraft?.registration || 'â€”'}
                        {project.firm_schedule && (
                          <svg className="w-3 h-3 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                            <path fillRule="evenodd" d="M5 9V7a5 5 0 0110 0v2a2 2 0 012 2v5a2 2 0 01-2 2H5a2 2 0 01-2-2v-5a2 2 0 012-2zm8-2v2H7V7a3 3 0 016 0z" clipRule="evenodd" />
                          </svg>
                        )}
                      </div>
                      <div className="text-xs text-gray-500 truncate">{project.workscope || (project.departments || ['MX']).join(', ')}</div>
                    </div>
                    {/* Start date â€” click to edit (read-only on mobile) */}
                    <div className={`${isMobile ? 'w-12' : 'w-20'} flex-shrink-0 flex items-center justify-center`}>
                      {!isMobile && editingDate?.projectId === project.id && editingDate?.field === 'arrival' ? (
                        <input
                          type="date"
                          autoFocus
                          value={editingDate.value}
                          onChange={e => setEditingDate(d => ({ ...d, value: e.target.value }))}
                          onBlur={() => {
                            if (editingDate.value && onUpdateProjectDates) onUpdateProjectDates(project.id, editingDate.value, project.departure_date)
                            setEditingDate(null)
                          }}
                          onKeyDown={e => {
                            if (e.key === 'Enter') { e.target.blur() }
                            if (e.key === 'Escape') setEditingDate(null)
                          }}
                          className="w-18 text-xs border border-brand-red/30 rounded px-1 py-0.5 focus:outline-none focus:ring-1 focus:ring-brand-red/20"
                        />
                      ) : (
                        <span
                          className={`text-xs font-medium font-accent ${arrivalOverdue ? 'text-red-600' : 'text-gray-500'} ${!isMobile && onUpdateProjectDates ? 'cursor-pointer hover:text-brand-red hover:underline' : ''}`}
                          onClick={!isMobile && onUpdateProjectDates ? () => setEditingDate({ projectId: project.id, field: 'arrival', value: project.arrival_date || '' }) : undefined}
                          title={!isMobile && onUpdateProjectDates ? 'Click to edit start date' : undefined}
                        >
                          {formatDateShort(project.arrival_date)}
                        </span>
                      )}
                    </div>
                    {/* End date â€” click to edit (read-only on mobile) */}
                    <div className={`${isMobile ? 'w-12' : 'w-20'} flex-shrink-0 flex items-center justify-center`}>
                      {!isMobile && editingDate?.projectId === project.id && editingDate?.field === 'departure' ? (
                        <input
                          type="date"
                          autoFocus
                          value={editingDate.value}
                          onChange={e => setEditingDate(d => ({ ...d, value: e.target.value }))}
                          onBlur={() => {
                            if (editingDate.value && onUpdateProjectDates) onUpdateProjectDates(project.id, project.arrival_date, editingDate.value)
                            setEditingDate(null)
                          }}
                          onKeyDown={e => {
                            if (e.key === 'Enter') { e.target.blur() }
                            if (e.key === 'Escape') setEditingDate(null)
                          }}
                          className="w-18 text-xs border border-brand-red/30 rounded px-1 py-0.5 focus:outline-none focus:ring-1 focus:ring-brand-red/20"
                        />
                      ) : (
                        <span
                          className={`text-xs font-medium font-accent ${departureOverdue ? 'text-red-600' : project.departure_date ? 'text-gray-500' : 'text-yellow-600'} ${!isMobile && onUpdateProjectDates ? 'cursor-pointer hover:text-brand-red hover:underline' : ''}`}
                          onClick={!isMobile && onUpdateProjectDates ? () => setEditingDate({ projectId: project.id, field: 'departure', value: project.departure_date || '' }) : undefined}
                          title={!isMobile && onUpdateProjectDates ? 'Click to edit end date' : undefined}
                        >
                          {project.departure_date ? formatDateShort(project.departure_date) : 'TBD'}
                        </span>
                      )}
                    </div>
                  </div>

                  <div className="flex-1 relative overflow-hidden">
                    {days.map((d, i) => d.isOff && (
                      <div key={i} className="absolute inset-y-0 pointer-events-none"
                        style={{ left: `${(i / totalDays) * 100}%`, width: `${(1 / totalDays) * 100}%`, backgroundColor: '#f3f4f6' }} />
                    ))}
                    {showToday && (
                      <div className="absolute inset-y-0 w-0.5 bg-brand-red z-10 pointer-events-none"
                        style={{ left: `${todayPct}%` }} />
                    )}
                    {inView && (
                      <GanttBar
                        segments={segments}
                        projectStart={clampedStart}
                        projectEnd={clampedEnd}
                        totalStart={viewStart.getTime()}
                        totalEnd={viewEnd.getTime()}
                        onClick={() => onClickProject(project)}
                        isTBD={isTBD}
                        showEndCap={showEndCap}
                        showStartCap={showStartCap}
                      />
                    )}
                    {/* Inspection due-date callouts (teal) + tolerance-expiry callouts (red).
                        Both types are merged per calendar day into one vertically-stacked,
                        centered container so they never float off-axis relative to the row. */}
                    {project.aircraft?.registration && (() => {
                      const reg = project.aircraft.registration
                      const tealEntries = inspectionDueDates[reg] || []
                      const redEntries  = toleranceExpiryDates[reg] || []
                      if (!tealEntries.length && !redEntries.length) return null

                      const totalMs = totalDays * 86400000
                      const byDay = {}
                      const addEntry = (entries, color) => {
                        for (const { label, date, fullName } of entries) {
                          const midnight = new Date(date); midnight.setHours(0, 0, 0, 0)
                          if (midnight < viewStart || midnight > viewEnd) continue
                          const key = midnight.getTime()
                          if (!byDay[key]) byDay[key] = { midnight, teal: [], red: [] }
                          byDay[key][color].push({ label, date, fullName })
                        }
                      }
                      addEntry(tealEntries, 'teal')
                      addEntry(redEntries,  'red')

                      return Object.entries(byDay).map(([key, { midnight, teal, red }]) => {
                        const pct = ((midnight.getTime() + 43200000 - viewStart.getTime()) / totalMs) * 100
                        const badgeStyle = (bg, title) => ({
                          padding: '1px 4px', borderRadius: 3,
                          backgroundColor: bg,
                          color: 'white', fontSize: 9, fontWeight: 700,
                          letterSpacing: '0.04em', whiteSpace: 'nowrap',
                          cursor: 'default', lineHeight: '14px',
                        })
                        return (
                          <div
                            key={`insp-day-${key}`}
                            style={{
                              position: 'absolute', left: `${pct}%`, top: '50%',
                              transform: 'translate(-50%, -50%)', zIndex: 6,
                              display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 2,
                            }}
                          >
                            {teal.length > 0 && (
                              <div style={{ display: 'flex', gap: 2 }}>
                                {teal.map(({ label, date, fullName }) => (
                                  <div
                                    key={`t-${label}`}
                                    title={`${fullName || INSP_FULL_NAMES[label] || label}\n${date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}`}
                                    style={badgeStyle('#0f766e')}
                                  >
                                    {label}
                                  </div>
                                ))}
                              </div>
                            )}
                            {red.length > 0 && (
                              <div style={{ display: 'flex', gap: 2 }}>
                                {red.map(({ label, date, fullName }) => (
                                  <div
                                    key={`r-${label}`}
                                    title={`${fullName || INSP_FULL_NAMES[label] || label} â€” tol. expires\n${date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}`}
                                    style={badgeStyle('#b91c1c')}
                                  >
                                    {label}
                                  </div>
                                ))}
                              </div>
                            )}
                          </div>
                        )
                      })
                    })()}

                    {/* Flight indicators â€” plane icons per date, dashes for away periods */}
                    {showFlights && project.aircraft?.registration && (() => {
                      const reg = project.aircraft.registration
                      const altReg = project.secondary_registration
                      const tripMap = flightsByReg[reg] || (altReg ? flightsByReg[altReg] : null) || {}
                      const totalMs = totalDays * 86400000

                      const legsByDate = {}
                      Object.values(tripMap).forEach(legs => {
                        legs.forEach(leg => {
                          if (!legsByDate[leg.leg_date]) legsByDate[leg.leg_date] = []
                          legsByDate[leg.leg_date].push(leg)
                        })
                      })

                      return (
                        <React.Fragment>
                          {(() => {
                            const allLegs = Object.values(tripMap).flat()
                              .sort((a, b) => (a.depart_dt||'').localeCompare(b.depart_dt||''))
                            const segs = []
                            for (let i = 0; i < allLegs.length - 1; i++) {
                              const leg = allLegs[i], next = allLegs[i + 1]
                              if (leg.leg_date === next.leg_date) continue
                              if (leg.destination === HOME_FBO || next.origin === HOME_FBO) continue
                              const aDate = new Date(leg.leg_date + 'T00:00:00')
                              const bDate = new Date(next.leg_date + 'T00:00:00')
                              if (bDate < viewStart || aDate > viewEnd) continue
                              const dashL = ((aDate.getTime() + 43200000 - viewStart.getTime()) / totalMs) * 100
                              const dashR = ((bDate.getTime() + 43200000 - viewStart.getTime()) / totalMs) * 100
                              segs.push(
                                <div key={i} style={{
                                  position: 'absolute', left: `${dashL}%`, width: `${dashR - dashL}%`,
                                  top: '50%', height: 0, borderTop: '1.5px dashed rgba(0,0,0,0.5)',
                                  transform: 'translateY(-50%)', zIndex: 4, pointerEvents: 'none',
                                }} />
                              )
                            }
                            // If the first known leg doesn't depart from home, the aircraft is
                            // already away â€” draw a one-day stub ending at the flight icon.
                            if (allLegs.length > 0 && allLegs[0].origin !== HOME_FBO) {
                              const firstDate = new Date(allLegs[0].leg_date + 'T00:00:00')
                              const stubEndMs = firstDate.getTime() + 43200000 // noon of leg day (icon position)
                              const stubStartMs = firstDate.getTime() - 86400000 // midnight of day before
                              if (stubEndMs > viewStart.getTime() && stubStartMs < viewEnd.getTime()) {
                                const stubL = ((Math.max(stubStartMs, viewStart.getTime()) - viewStart.getTime()) / totalMs) * 100
                                const stubR = ((stubEndMs - viewStart.getTime()) / totalMs) * 100
                                segs.push(
                                  <div key="away-start" style={{
                                    position: 'absolute', left: `${stubL}%`, width: `${stubR - stubL}%`,
                                    top: '50%', height: 0, borderTop: '1.5px dashed rgba(0,0,0,0.5)',
                                    transform: 'translateY(-50%)', zIndex: 4, pointerEvents: 'none',
                                  }} />
                                )
                              }
                            }
                            return segs
                          })()}

                          {Object.entries(legsByDate).map(([date, legs]) => {
                            const legDate = new Date(date + 'T00:00:00')
                            if (legDate < viewStart || legDate > viewEnd) return null
                            const pct = ((legDate.getTime() + 43200000 - viewStart.getTime()) / totalMs) * 100
                            const sortedLegs = [...legs].sort((a, b) => (a.depart_dt||'').localeCompare(b.depart_dt||''))
                            return (
                              <div key={date}
                                title={legsTooltip(sortedLegs)}
                                style={{
                                  position: 'absolute', left: `${pct}%`, top: '50%',
                                  transform: 'translate(-50%,-50%)', zIndex: 5,
                                  width: 18, height: 18, backgroundColor: '#101820', borderRadius: 9,
                                  display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'default',
                                }}
                              >
                                <svg width="10" height="10" viewBox="0 0 24 24" fill="#ffffff">
                                  <path d="M21 16v-2l-8-5V3.5c0-.83-.67-1.5-1.5-1.5S10 2.67 10 3.5V9l-8 5v2l8-2.5V19l-2 1.5V22l3.5-1 3.5 1v-1.5L13 19v-5.5l8 2.5z"/>
                                </svg>
                              </div>
                            )
                          })}
                        </React.Fragment>
                      )
                    })()}

                  </div>
                </div>

                {/* Subtask rows â€” grouped by status */}
                {isExpanded && [
                  { status: 'Complete',  label: 'Complete',  labelColor: 'text-green-600' },
                  { status: 'In Work',   label: 'In Work',   labelColor: 'text-blue-600'  },
                  { status: 'Scheduled', label: 'Scheduled', labelColor: 'text-gray-500'  },
                ].map(group => {
                  const groupSubtasks = visibleSubtasks.filter(s => s.status === group.status)
                  if (groupSubtasks.length === 0) return null
                  const groupOpen = isGroupOpen(project.id, group.status)
                  return (
                    <React.Fragment key={group.status}>
                      {/* Group header row */}
                      <div className="flex border-b border-gray-100 bg-gray-50/60"
                        style={{ height: SUBROW_HEIGHT }}>
                        <div className="flex-shrink-0 border-r border-gray-200 flex items-center" style={{ width: LABEL_WIDTH }}>
                          <div className="w-6 flex-shrink-0" />
                          <button onClick={() => toggleGroup(project.id, group.status)}
                            className="w-4 h-4 flex items-center justify-center text-gray-400 hover:text-gray-600 flex-shrink-0">
                            <svg className={`w-3 h-3 transition-transform ${groupOpen ? 'rotate-90' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                            </svg>
                          </button>
                          <span className={`text-xs font-semibold ml-1.5 ${group.labelColor}`}>{group.label}</span>
                          <span className="text-xs text-gray-400 ml-1">({groupSubtasks.length})</span>
                        </div>
                        <div className="flex-1 bg-gray-50/40" />
                      </div>

                      {/* Subtask rows within group */}
                      {groupOpen && [...groupSubtasks].sort((a, b) => (a.start_date || '').localeCompare(b.start_date || '')).map(subtask => {
                        const sStart = new Date(subtask.start_date + 'T00:00:00'); sStart.setHours(0, 0, 0, 0)
                        const sEnd = new Date(subtask.end_date + 'T00:00:00'); sEnd.setHours(0, 0, 0, 0)
                        const sEndDisplay = new Date(sEnd.getTime() + 86400000)
                        const sInView = sEndDisplay >= viewStart && sStart <= viewEnd
                        const color = orgDeptMap[subtask.department]?.hex || '#9ca3af'
                        const totalMs = viewEnd.getTime() - viewStart.getTime()
                        const sClampedStart = Math.max(sStart.getTime(), viewStart.getTime())
                        const sClampedEnd = Math.min(sEndDisplay.getTime(), viewEnd.getTime())
                        const left = ((sClampedStart - viewStart.getTime()) / totalMs) * 100
                        const width = Math.max(((sClampedEnd - sClampedStart) / totalMs) * 100, 0.3)
                        return (
                          <div key={subtask.id} className="flex border-b border-gray-100 hover:bg-brand-red/5 transition-colors"
                            style={{ height: SUBROW_HEIGHT }}>
                            <div className="flex-shrink-0 border-r border-gray-200 flex items-center bg-white" style={{ width: LABEL_WIDTH }}>
                              <div className={`${isMobile ? 'w-6' : 'w-10'} flex-shrink-0`} />
                              <div className="w-4 h-4 flex items-center justify-center flex-shrink-0">
                                <div className="w-1.5 h-1.5 rounded-full" style={{ backgroundColor: color }} />
                              </div>
                              <div className="flex-shrink-0 pr-2" style={{ width: isMobile ? 64 : 108 }}>
                                <span className="text-xs text-gray-600 truncate block">{subtask.task_name}</span>
                              </div>
                              <div className={`${isMobile ? 'w-12' : 'w-20'} flex-shrink-0 text-xs text-gray-400 flex items-center justify-center`}>
                                {formatDateShort(subtask.start_date)}
                              </div>
                              <div className={`${isMobile ? 'w-12' : 'w-20'} flex-shrink-0 text-xs text-gray-400 flex items-center justify-center`}>
                                {formatDateShort(subtask.end_date)}
                              </div>
                            </div>
                            <div className="flex-1 relative overflow-hidden">
                              {days.map((d, i) => d.isOff && (
                                <div key={i} className="absolute inset-y-0 pointer-events-none"
                                  style={{ left: `${(i / totalDays) * 100}%`, width: `${(1 / totalDays) * 100}%`, backgroundColor: '#f3f4f6' }} />
                              ))}
                              {showToday && (
                                <div className="absolute inset-y-0 w-0.5 bg-brand-red z-10 pointer-events-none"
                                  style={{ left: `${todayPct}%` }} />
                              )}
                              {sInView && (
                                <div
                                  className="absolute top-1/2 -translate-y-1/2 rounded cursor-pointer hover:opacity-80 transition-opacity shadow-sm"
                                  style={{ left: `${left}%`, width: `${width}%`, height: 18, backgroundColor: color, minWidth: 4 }}
                                  onClick={() => onClickSubtask(subtask)}
                                />
                              )}
                            </div>
                          </div>
                        )
                      })}
                    </React.Fragment>
                  )
                })}
              </div>
            )
          })}
        </div>
      </div>
    </div>
  )
}
